import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HelpCircle, X, Mail, KeyRound, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { NeptuneLogo } from "../../components/neptune/NeptuneMark";

function AdminAccessHelpModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      data-testid="admin-help-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-7 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#00E5FF]" />
            <h2 className="text-[15px] font-medium text-white">Can&apos;t sign in?</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-help-close-btn"
            className="text-zinc-500 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[12.5px] text-zinc-400 leading-relaxed mb-4">
          Admin accounts use <span className="text-white">Google sign-in</span> only —
          there&apos;s no Neptune-managed password to reset. Pick the option that matches
          your situation:
        </p>

        <div className="space-y-3 mb-5">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="admin-help-google-section">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
              — Forgot Google password
            </div>
            <p className="text-[12.5px] text-zinc-300 mb-2">
              Reset it at Google directly, then come back and click &quot;Continue with Google&quot;.
            </p>
            <a
              href="https://accounts.google.com/signin/v2/recoveryidentifier?service=mail"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-help-google-recover-link"
              className="inline-flex items-center gap-1.5 text-[12px] text-[#00E5FF] hover:text-white"
            >
              Recover Google account
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="admin-help-locked-section">
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
              — Locked out / account not authorized
            </div>
            <p className="text-[12.5px] text-zinc-300 mb-2">
              If Google sign-in works but the console rejects your account, your address
              isn&apos;t whitelisted yet. Email the team to get added.
            </p>
            <a
              href="mailto:support@neptunepay.io?subject=Admin%20access%20request"
              data-testid="admin-help-support-link"
              className="inline-flex items-center gap-1.5 text-[12px] text-[#00E5FF] hover:text-white"
            >
              <Mail className="h-3 w-3" />
              support@neptunepay.io
            </a>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          data-testid="admin-help-ok-btn"
          className="w-full rounded-xl px-4 py-2.5 text-[12.5px] bg-white text-black hover:bg-zinc-200"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin/dashboard", { replace: true });
  }, [user, loading, navigate]);

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white font-body relative overflow-hidden flex items-center justify-center px-6">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[1000px] rounded-full bg-[#0055FF]/22 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-[-20%] right-[-10%] h-[400px] w-[400px] rounded-full bg-[#00E5FF]/12 blur-[140px]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="flex justify-center mb-12">
          <NeptuneLogo height={28} />
        </div>

        <div className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01] backdrop-blur-2xl p-9 sm:p-11 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Console</div>
          <h1 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
            Sign in to Neptune CMS
          </h1>
          <p className="mt-4 text-[14px] text-zinc-400 leading-relaxed max-w-sm mx-auto">
            Internal access only. Authentication handled via Google.
          </p>

          <button
            type="button"
            onClick={login}
            data-testid="admin-login-google-btn"
            className="mt-9 group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors w-full px-5 py-3.5 text-[14px] font-medium"
          >
            Continue with Google
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>

          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            data-testid="admin-help-link"
            className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-[#00E5FF] transition-colors"
          >
            <HelpCircle className="h-3 w-3" />
            Forgot password? Can&apos;t sign in?
          </button>

          <div className="mt-8 text-[11px] text-zinc-600 tracking-[0.2em] uppercase">
            Restricted · Authorized accounts only
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-[12.5px] text-zinc-500 hover:text-white transition-colors">
            ← Back to Neptune
          </a>
        </div>
      </motion.div>

      <AdminAccessHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </main>
  );
}
